import * as React from 'react';
import {providers, client} from '@logilab/cwclientlibjs';
import {useSelection} from './useSelection';
import {getNamedEntityFromList} from '../commons';
import {selectWithTextSearch} from './SearchBar';

export type RelationOrAttribute =
    | providers.EntityRelationSchema
    | providers.EntityAttributeSchema;

export interface SchemaNavigatorProps {
    schemaProvider: providers.RqlEntitySchemasLoader;
    handleError: (error: string) => void;
    showMetamodel: boolean;
    clientRql: client.CwRqlClient;
    entityTypeTextSearch: string;
    setEntityTypeTextSearch: (text: string) => void;
    relationsAndAttributesTextSearch: string;
    setRelationsAndAttributesTextSearch: (text: string) => void;
    linkedEntityTypeTextSearch: string;
    setLinkedEntityTypeTextSearch: (text: string) => void;
}

const ENTITY_TYPES_METAMODEL = [
    'TZTime',
    'Date',
    'Decimal',
    'Interval',
    'Bytes',
    'Datetime',
    'String',
    'TZDatetime',
    'BigInt',
    'Time',
    'Int',
    'Float',
    'Boolean',
    'Password',
    'CWRType',
    'CWAttribute',
    'CWRelation',
    'CWComputedRType',
    'CWConstraint',
    'CWConstraintType',
    'CWUniqueTogetherConstraint',
    'RQLExpression',
    'Transition',
    'TrInfo',
    'WorkflowTransition',
    'BaseTransition',
    'SubWorkflowExitPoint',
    'CWProperty',
    'ExternalUri',
    'CWDataImport',
    'CWSource',
    'CWSourceHostConfig',
    'CWSession',
];

const RELATIONS_AND_ATTRIBUTES_METAMODEL = [
    'owned_by',
    'created_by',
    'is',
    'is_instance_of',
    'identity',
    'eid',
    'creation_date',
    'cw_source',
    'modification_date',
    'has_text',
    'cwuri',
    'custom_workflow',
    'wf_info_for',
    'transition_of',
    'initial_state',
    'default_workflow',
    'allowed_transition',
    'destination_state',
    'from_state',
    'to_state',
    'condition',
    'subworkflow',
    'subworkflow_state',
    'subworkflow_exit',
    'by_transition',
    'in_group',
    'require_group',
    'for_user',
    'cw_import_of',
    'cw_host_config_of',
    'cw_source',
    'subject',
    'specializes',
    'relation_type',
    'from_entity',
    'to_entity',
    'constrained_by',
    'cstrtype',
    'constraint_of',
    'relations',
    'read_permission',
    'add_permission',
    'delete_permission',
    'update_permission',
];

interface SchemaNavigatorState {
    schema: providers.Schema | null;
    allRelations: providers.EntityRelationSchema[] | null;
    allAttributes: providers.EntityAttributeSchema[] | null;
    tempSelectedEntityTypeName: string | null;
    tempSelectedRelationOrAttributeName: string | null;
    selectedEntityTypeName: string | null;
    selectedRelationName: string | null;
    selectedAttributeName: string | null;
    selectedLinkedEntityTypeName: string | null;
}

/** Filter Meta model relations */

function filterMetaModelRelations(
    relations: providers.EntityRelationSchema[],
    showMetamodel: boolean
): providers.EntityRelationSchema[] {
    return showMetamodel
        ? relations
        : relations.filter(
              (relation) =>
                  !RELATIONS_AND_ATTRIBUTES_METAMODEL.includes(relation.name)
          );
}

/** Filter Meta model attributes */

function filterMetaModelAttributes(
    attributes: providers.EntityAttributeSchema[],
    showMetamodel: boolean
): providers.EntityAttributeSchema[] {
    return showMetamodel
        ? attributes
        : attributes.filter(
              (attribute) =>
                  !RELATIONS_AND_ATTRIBUTES_METAMODEL.includes(attribute.name)
          );
}

/** Filter Meta model entity types */

function filterMetaModelEntityTypes(
    entities: providers.EntitySchema[],
    showMetamodel: boolean
): providers.EntitySchema[] {
    return showMetamodel
        ? entities
        : entities.filter(
              (entityType) => !ENTITY_TYPES_METAMODEL.includes(entityType.name)
          );
}

/** Filter entity types based on schema navigator
 * selection state
 */

function filterEntityTypes(
    {
        selectedEntityTypeName,
        selectedRelationName,
        selectedAttributeName,
        tempSelectedRelationOrAttributeName,
        schema,
        allAttributes,
    }: SchemaNavigatorState,
    showMetamodel: boolean
): providers.EntitySchema[] {
    if (schema === null || allAttributes === null) {
        return [];
    }
    const metamodelFilteredEntities = filterMetaModelEntityTypes(
        schema.entities,
        showMetamodel
    );
    // If a relation or attribute is temporarily selected
    if (
        tempSelectedRelationOrAttributeName !== null &&
        selectedEntityTypeName === null
    ) {
        // if temp selectedRelation is an attribute
        const tempSelectedAttribute = getNamedEntityFromList(
            tempSelectedRelationOrAttributeName,
            allAttributes
        );
        if (tempSelectedAttribute !== null) {
            return metamodelFilteredEntities.filter((entityType) =>
                entityType.attributes.some(
                    (attribute) =>
                        attribute.name === tempSelectedRelationOrAttributeName
                )
            );
        }
        // otherwise it is a relation
        else {
            return metamodelFilteredEntities.filter((entityType) =>
                entityType.relationsTo.some(
                    (relTo) =>
                        relTo.name === tempSelectedRelationOrAttributeName
                )
            );
        }
    }

    // in the case of no temporary selection
    if (selectedRelationName !== null && selectedEntityTypeName === null) {
        return metamodelFilteredEntities.filter((entityType) =>
            entityType.relationsTo.some(
                (relTo) => relTo.name === selectedRelationName
            )
        );
    }
    if (selectedAttributeName !== null && selectedEntityTypeName === null) {
        return metamodelFilteredEntities.filter((entityType) =>
            entityType.attributes.some(
                (attribute) => attribute.name === selectedAttributeName
            )
        );
    }

    return metamodelFilteredEntities;
}

/** Filter relations and attributes based
 * on schema navigator selection state
 */

function filterRelationsAndAttributes(
    {
        schema,
        allRelations,
        allAttributes,
        tempSelectedEntityTypeName,
        selectedEntityTypeName,
        selectedLinkedEntityTypeName,
    }: SchemaNavigatorState,
    showMetamodel: boolean
): {
    relations: {
        from: providers.EntityRelationSchema[];
        to: providers.EntityRelationSchema[];
    };
    attributes: providers.EntityAttributeSchema[];
} {
    if (allRelations === null || schema === null) {
        return {
            relations: {from: [], to: []},
            attributes: [],
        };
    }

    const currentSelectedEntityTypeName =
        tempSelectedEntityTypeName ?? selectedEntityTypeName ?? null;

    // If no entity type is selected, display all relations and attributes
    if (currentSelectedEntityTypeName === null) {
        return {
            relations: {
                from: [],
                to: filterMetaModelRelations(allRelations, showMetamodel),
            },
            attributes:
                allAttributes !== null
                    ? filterMetaModelAttributes(allAttributes, showMetamodel)
                    : [],
        };
    }

    // If an entity type is selected, compute relations from and to this entity type
    let from = [];
    let to = [];

    //Get CurrentSelectedEntityType from name
    const currentSelectedEntityType = getNamedEntityFromList(
        currentSelectedEntityTypeName,
        schema.entities
    );

    for (const rel of filterMetaModelRelations(allRelations, showMetamodel)) {
        // Select a relation if it is in the way currentSelectedEntityType To another entity type
        if (
            currentSelectedEntityType !== null &&
            currentSelectedEntityType.relationsTo.some(
                (relTo) => relTo.name === rel.name
            )
        ) {
            to.push(rel);
        }

        // Select a relation if it is in the way an entity type To the currentSelectedEntityType
        // and that the entity type is not the currentSelectedEntityType to avoid doubles in loop relations
        if (
            currentSelectedEntityType !== null &&
            currentSelectedEntityType.relationsFrom.some(
                (relFrom) =>
                    relFrom.name === rel.name && relFrom.to !== relFrom.from
            )
        ) {
            from.push(rel);
        }
    }

    // If a linked entity type is selected, remove the relations which do not
    // point towards this entity type

    if (selectedLinkedEntityTypeName !== null) {
        const selectedLinkedEntityType = getNamedEntityFromList(
            selectedLinkedEntityTypeName,
            schema.entities
        );
        from = from.filter((rel) =>
            selectedLinkedEntityType!.relationsTo.some(
                (relFrom) => rel.name === relFrom.name
            )
        );
        to = to.filter((rel) =>
            selectedLinkedEntityType!.relationsFrom.some(
                (relTo) => rel.name === relTo.name
            )
        );
    }

    // If an entity type is selected, compute attributes of this entity type
    let filteredAttributes: providers.EntityAttributeSchema[];

    if (currentSelectedEntityType !== null) {
        // If currentSelectedEntityType is not a temp selection
        // And a linked entity type is selected
        // Filter out all attributes
        if (
            tempSelectedEntityTypeName === null &&
            selectedLinkedEntityTypeName !== null
        ) {
            filteredAttributes = [];
        }
        // otherwise, retrieve currentSelectedEntity types attributes
        else {
            filteredAttributes = filterMetaModelAttributes(
                currentSelectedEntityType.attributes,
                showMetamodel
            );
        }
    } else {
        filteredAttributes = [];
    }

    return {
        relations: {from, to},
        attributes: filteredAttributes,
    };
}

/**
 * Filter EntityTypes based on the schema navigator selection state
 */
function filterLinkedEntityTypes(
    tempAllLinkedEntityTypes: Map<string, providers.EntitySchema>,
    isFrom: boolean = true,
    {schema, selectedEntityTypeName}: SchemaNavigatorState,
    currentSelectedRelationName: string | null,
    showMetamodel: boolean
): providers.EntitySchema[] {
    if (schema === null || selectedEntityTypeName === null) {
        return [];
    }
    const selectedEntityType = getNamedEntityFromList(
        selectedEntityTypeName,
        schema.entities
    );

    // For each entity type of the schema (filtered by metamodel)
    return filterMetaModelEntityTypes(schema.entities, showMetamodel).filter(
        (entityType) =>
            // check in the from or to relations array of the selected entity type
            (isFrom
                ? selectedEntityType!.relationsFrom
                : selectedEntityType!.relationsTo
            ).some((rel: providers.EntityRelationSchema) => {
                // if metamodel is not shown and rel is in metamodel
                // Filter out entityType
                if (
                    !showMetamodel &&
                    RELATIONS_AND_ATTRIBUTES_METAMODEL.includes(rel.name)
                ) {
                    return false;
                }

                // if entityType is already in list of linked entity type
                // filter out entityType
                if (tempAllLinkedEntityTypes.has(entityType.name)) {
                    return false;
                }

                // else if no relation is selected include entityType
                // or if the selected relation is rel
                if (
                    currentSelectedRelationName === null ||
                    currentSelectedRelationName === rel.name
                ) {
                    // check if entityType is in rel object or subject given isFrom
                    if (
                        (isFrom && rel.from === entityType.eid) ||
                        (!isFrom && rel.to === entityType.eid)
                    ) {
                        //include entityType in list of linked entity types
                        tempAllLinkedEntityTypes.set(
                            entityType.name,
                            entityType
                        );
                        return true;
                    }
                }

                // else
                return false;
            })
    );
}

function getLinkedEntityTypes(
    state: SchemaNavigatorState,
    showMetamodel: boolean
): {
    from: providers.EntitySchema[];
    to: providers.EntitySchema[];
} {
    // If the schema is null,
    // or if nothing is selected return empty arrays
    if (
        state.schema === null ||
        state.allRelations === null ||
        (state.selectedRelationName === null &&
            state.tempSelectedRelationOrAttributeName === null &&
            (state.selectedEntityTypeName === null ||
                (state.tempSelectedEntityTypeName !== null &&
                    state.tempSelectedEntityTypeName !==
                        state.selectedEntityTypeName)))
    ) {
        return {
            from: [],
            to: [],
        };
    }

    let currentSelectedRelationName: string | null = null;

    // If there is a tempSelectedAttributeOrRelation
    if (state.tempSelectedRelationOrAttributeName !== null) {
        const tempSelectedRelation = getNamedEntityFromList(
            state.tempSelectedRelationOrAttributeName,
            state.allRelations
        );
        // If tempSelectedAttributeOrRelation is a relation
        if (tempSelectedRelation !== null) {
            currentSelectedRelationName =
                state.tempSelectedRelationOrAttributeName;
        }
        // otherwise tempRelationOrAttribute is an attribute
        // return empty arrays
        else {
            return {
                from: [],
                to: [],
            };
        }
    }
    // else :  if there is no tempSelectedAttributeOrRelation
    else {
        // If an attribute is selected return empty arrays
        if (state.selectedAttributeName !== null) {
            return {
                from: [],
                to: [],
            };
        }
        currentSelectedRelationName = state.selectedRelationName;
    }

    // If a relation but no entity type is selected (or temporarily selected)
    // return all entity types which are in the object
    // of the selected relation
    if (
        currentSelectedRelationName !== null &&
        state.selectedEntityTypeName === null
    ) {
        return {
            from: [],
            to: filterMetaModelEntityTypes(
                state.schema.entities,
                showMetamodel
            ).filter((entityType) =>
                entityType.relationsFrom.some(
                    (relFrom) => relFrom.name === currentSelectedRelationName
                )
            ),
        };
    }

    const tempAllLinkedEntityTypes = new Map();

    return {
        from: filterLinkedEntityTypes(
            tempAllLinkedEntityTypes,
            true,
            state,
            currentSelectedRelationName,
            showMetamodel
        ),
        to: filterLinkedEntityTypes(
            tempAllLinkedEntityTypes,
            false,
            state,
            currentSelectedRelationName,
            showMetamodel
        ),
    };
}

export function useSchemaNavigator({
    schemaProvider,
    handleError,
    showMetamodel,
    clientRql,
    entityTypeTextSearch,
    setEntityTypeTextSearch,
    relationsAndAttributesTextSearch,
    setRelationsAndAttributesTextSearch,
    linkedEntityTypeTextSearch,
    setLinkedEntityTypeTextSearch,
}: SchemaNavigatorProps) {
    const [schema, setSchema] = React.useState<providers.Schema | null>(null);
    const [allRelations, setAllRelations] = React.useState<
        providers.EntityRelationSchema[] | null
    >(null);
    const [allAttributes, setAllAttributes] = React.useState<
        providers.EntityAttributeSchema[] | null
    >(null);

    const {
        selectedEntityName: tempSelectedEntityTypeName,
        setSelectedEntityName: setTempEntityTypeName,
        deselectEntity: deselectTempEntityType,
    } = useSelection();
    const {
        selectedEntityName: tempSelectedRelationOrAttributeName,
        setSelectedEntityName: setTempSelectedRelationOrAttribute,
        deselectEntity: deselectTempRelationOrAttribute,
    } = useSelection();
    const {
        selectedEntityName: selectedEntityTypeName,
        setSelectedEntityName: setSelectedEntityType,
        deselectEntity: deselectEntityType,
    } = useSelection();
    const {
        selectedEntityName: selectedRelationName,
        setSelectedEntityName: setSelectedRelation,
        deselectEntity: deselectRelation,
    } = useSelection();
    const {
        selectedEntityName: selectedAttributeName,
        setSelectedEntityName: setSelectedAttribute,
        deselectEntity: deselectAttribute,
    } = useSelection();
    const {
        selectedEntityName: selectedLinkedEntityTypeName,
        setSelectedEntityName: setSelectedLinkedEntityType,
        deselectEntity: deselectLinkedEntityType,
    } = useSelection();

    const [selectedAttributeValues, setSelectedAttributeValues] =
        React.useState<any[] | null>(null);

    const state = {
        schema,
        allRelations,
        allAttributes,
        tempSelectedEntityTypeName,
        selectedEntityTypeName,
        selectedRelationName,
        selectedAttributeName,
        selectedLinkedEntityTypeName,
        tempSelectedRelationOrAttributeName,
    };
    const [schemaLoaded, setSchemaLoaded] = React.useState<boolean>(false);

    const {relations, attributes} = filterRelationsAndAttributes(
        state,
        showMetamodel
    );
    const {from, to} = getLinkedEntityTypes(state, showMetamodel);
    const filteredLinkedEntityTypes = from.concat(to);
    const filteredEntityTypes = filterEntityTypes(state, showMetamodel);

    // Load Schema
    React.useEffect(() => {
        schemaProvider
            .load()
            .then((schema: providers.Schema) => {
                setSchema(schema);
                const allRelations = new Map();
                const allAttributes = new Map();

                for (const eType of schema.entities) {
                    for (const rel of eType.relationsFrom.concat(
                        eType.relationsTo
                    )) {
                        allRelations.set(rel.name, rel);
                    }
                    for (const attr of eType.attributes) {
                        allAttributes.set(attr.name, attr);
                    }
                }
                setSchemaLoaded(true);
                setAllRelations(Array.from(allRelations.values()));
                setAllAttributes(Array.from(allAttributes.values()));
            })
            .catch((e: Error) => {
                setSchemaLoaded(false);
                handleError(e.message);
            });
    }, [schemaProvider, handleError]);

    // Get ten first attribute values
    React.useEffect(() => {
        if (selectedAttributeName !== null) {
            const attribute = getNamedEntityFromList(
                selectedAttributeName,
                allAttributes ?? []
            );
            // Do not retrieve Bytes values
            if (attribute !== null && attribute.type === 'Bytes') {
                setSelectedAttributeValues(null);
                return;
            }
            let query = `DISTINCT Any X LIMIT 10 WHERE Y ${selectedAttributeName} X`;
            if (selectedEntityTypeName !== null) {
                query += `, Y is ${selectedEntityTypeName}`;
            }
            clientRql
                .queryRows(query)
                .then((rows) => {
                    setSelectedAttributeValues([].concat.apply([], rows));
                })
                .catch((reason) => {
                    console.error(reason);
                    setSelectedAttributeValues(null);
                });
        } else {
            setSelectedAttributeValues(null);
        }
    }, [
        allAttributes,
        clientRql,
        selectedAttributeName,
        selectedEntityTypeName,
    ]);

    // Select entity type with  text search
    React.useEffect(() => {
        deselectEntityType();

        selectWithTextSearch(
            entityTypeTextSearch,
            schema !== null ? schema.entities : [],
            setSelectedEntityType
        );
    }, [
        deselectEntityType,
        entityTypeTextSearch,
        schema,
        setSelectedEntityType,
    ]);

    // Select linked entity type with text search
    React.useEffect(() => {
        deselectLinkedEntityType();

        selectWithTextSearch(
            linkedEntityTypeTextSearch,
            schema !== null ? schema.entities : [],
            setSelectedLinkedEntityType
        );
    }, [
        deselectLinkedEntityType,
        linkedEntityTypeTextSearch,
        setSelectedLinkedEntityType,
        schema,
    ]);

    // Select relation or attribute with text search
    React.useEffect(() => {
        deselectRelation();
        deselectAttribute();

        // create a single list of relations and attributes
        let relationsAndAttributeNames: RelationOrAttribute[] = [
            ...(allRelations ?? []),
            ...(allAttributes ?? []),
        ];
        selectWithTextSearch(
            relationsAndAttributesTextSearch,
            relationsAndAttributeNames,
            (name) => {
                //if name is an attribute name
                if (
                    getNamedEntityFromList(name, allAttributes ?? []) !== null
                ) {
                    setSelectedAttribute(name);
                }
                //else
                else {
                    setSelectedRelation(name);
                }
            }
        );
    }, [
        allAttributes,
        allRelations,
        deselectAttribute,
        deselectRelation,
        relationsAndAttributesTextSearch,
        setSelectedAttribute,
        setSelectedRelation,
    ]);

    return {
        schemaLoaded,
        filteredEntityTypes,
        relations,
        attributes,
        filteredLinkedEntityTypes,
        selectedAttributeValues,
        selectedEntityTypeName,
        selectedRelationName,
        selectedAttributeName,
        selectedLinkedEntityTypeName,
        tempSelectedEntityTypeName,
        setTempEntityTypeName,
        deselectTempEntityType,
        tempSelectedRelationOrAttributeName,
        setTempSelectedRelationOrAttribute,
        deselectTempRelationOrAttribute,
    };
}
